<template>
    <section class="sites-list-stack" v-if="cards && cards.length > 0">
        <div class="container">
            <header class="component-header" v-if="showHeader">
                <h2 v-html="title" class="headline-1"></h2>
            </header>

            <ul class="stack">
                <li v-for="card in visibleCards" :key="card.id">
                    <CardsSite type="list" :item="card" :itinerary="itinerary" @updateSingleItem="updateSingleItem" :isGhost="isGhost" />
                </li>
            </ul>
        </div>
    </section>
</template>

<script setup>
    // Emits
    const emit = defineEmits(['updateSingleItem']);

    // Props
    const props = defineProps({
        itinerary: {
            type: Object,
            required: false,
            default: {},
        },

        cards: {
            type: Array,
            required: true,
            default: [],
        },

        showHeader: {
            type: Boolean,
            required: false,
            default: true,
        },

        componentLocation: {
            type: String,
            required: false,
            default: '',
        },
        isGhost: {
            type: Boolean,
            required: false,
            default: false,
        },
    });
    const { isGhost } = toRefs(props);
    const { itinerary, cards, showHeader, componentLocation } = toRefs(props);

    // Rendering every card at once froze the tap that opened /sites: 207 cards
    // took one ~560ms main-thread task on a phone-speed CPU, with no paint until
    // it ended. On client-side visits the first INITIAL_CARDS render
    // straight away and the rest follow in CARD_BATCH-sized steps, a frame
    // apart. Three cases render the whole list at once instead:
    //  - hydrating server HTML, which already contains every card (a partial
    //    list would mismatch it, and crawlers need every site link);
    //  - back/forward to this page (vue-router saved a scroll position in
    //    history.state), so the scroll can be restored past the first batch;
    //  - on the server.
    const INITIAL_CARDS = 24;
    const CARD_BATCH = 24;
    const nuxtApp = useNuxtApp();
    const restoringScroll = import.meta.client && (window.history.state?.scroll?.top ?? 0) > 0;
    const renderAll = import.meta.server || nuxtApp.isHydrating || restoringScroll;
    const visibleCount = ref(renderAll ? Infinity : INITIAL_CARDS);
    const visibleCards = computed(() => (visibleCount.value >= cards.value.length ? cards.value : cards.value.slice(0, visibleCount.value)));

    let rafId = 0;
    let timeoutId = 0;
    let growing = false;
    function growUntilComplete() {
        if (growing || visibleCount.value >= cards.value.length) return;
        growing = true;
        rafId = requestAnimationFrame(() => {
            // setTimeout after the frame so each batch runs after that frame's
            // paint, not inside it.
            timeoutId = window.setTimeout(() => {
                growing = false;
                visibleCount.value += CARD_BATCH;
                growUntilComplete();
            }, 0);
        });
    }

    onMounted(growUntilComplete);
    // A filter change can lengthen the list while it is still growing.
    watch(() => cards.value.length, growUntilComplete);
    onBeforeUnmount(() => {
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId);
    });

    // Computed
    const total = computed(() => {
        return cards.value.length || 0;
    });

    const title = computed(() => {
        if (componentLocation && componentLocation.value) {
            switch (componentLocation.value.toLowerCase()) {
                case 'neighborhood':
                    return `Sites In This Neighborhood (${total.value})`;

                case 'itinerary':
                    return `Sites On This Itinerary (${total.value})`;
            }
        }
    });

    // Methods
    function updateSingleItem(item) {
        emit('updateSingleItem', item);
    }
</script>

<style lang="scss" scoped>
    .sites-list-stack {

        @media (min-width: $tablet) {
            .stack {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                grid-column-gap: 20px;
                grid-row-gap: 40px;
            }
        }
    }

    .itinerary-detail .sites-list-stack .container {
        padding: 32px 16px 0 16px;

        @media (min-width: $tablet) {
            padding: 32px 0 32px 0;
        }

    }
</style>
